import { useContext, useEffect, useRef, useState } from "react";

import AuthContext from "context/auth/authContext";
import ParamsContext from "context/params/paramsContext";
import {
    API_IS_BEING_CANCELLED,
    getStringifiedParams,
    Query,
    useApi
} from "shared";

import {
    getAdditionalQueryInfo,
    getCompanySelectorParams,
    getDescription,
    getFilteredData,
    getQueryParams,
    getResourceParams,
    getSortParams,
    handleInitialOptionsLoad
} from "../functions";
import { UseQueryDropdownOptions } from "../types";

export const useQueryDropdown = (parameters: UseQueryDropdownOptions) => {
    const { queryParams: allQueryParams } = useContext(ParamsContext);
    const { isAdminUser } = useContext(AuthContext);

    const { queryData, handleResponse, cancelSource, isCanceled } = useApi();

    const {
        resource,
        idType,
        description,
        exceptions,
        hasOnlyInternalCompanies,
        hasNotAssigned,
        hasUploadedFile,
        isRootRequired,
        isCompanyIdRequired,
        hasNoPermission,
        onInitialOptionsLoad
    } = parameters;

    const lastResourceRef = useRef<string | null>(null);

    const [isLoading, setIsLoading] = useState(
        !hasNoPermission && Boolean(resource)
    );
    const [query, setQuery] = useState("");
    const [options, setOptions] = useState<Query[]>([]);

    useEffect(() => {
        let isSubscribed = true;

        const fetchData = async () => {
            if (resource) {
                try {
                    setIsLoading(true);

                    const [resourceName, resourceStringParams] =
                        resource.split("?");

                    const resourceParams =
                        getResourceParams(resourceStringParams);

                    const queryParams = getQueryParams(query);

                    const sortParams = getSortParams(resourceName, isAdminUser);

                    const companySelectorParams = getCompanySelectorParams(
                        allQueryParams,
                        isRootRequired,
                        isCompanyIdRequired
                    );

                    const params = getStringifiedParams({
                        ...resourceParams,
                        ...sortParams,
                        ...queryParams,
                        ...companySelectorParams
                    });

                    const {
                        data: { data }
                    } = await queryData(resourceName, params);

                    if (isSubscribed) {
                        const queryResult: Query[] = data.map(
                            (singleItem: any) => {
                                return {
                                    id: singleItem[idType],
                                    name: singleItem.name,
                                    attributes: singleItem.attributes,
                                    ...getDescription(singleItem, description),
                                    ...getAdditionalQueryInfo(
                                        singleItem,
                                        resourceName
                                    )
                                };
                            }
                        );

                        const filteredData = getFilteredData(
                            queryResult,
                            exceptions,
                            hasOnlyInternalCompanies,
                            hasNotAssigned
                        );

                        setOptions(filteredData);

                        handleInitialOptionsLoad(
                            lastResourceRef,
                            filteredData,
                            resource,
                            onInitialOptionsLoad
                        );

                        setIsLoading(false);
                    }
                } catch (error) {
                    if (isSubscribed && !isCanceled(error)) {
                        handleResponse(error);
                        setIsLoading(false);
                    }
                }
            }
        };

        !hasNoPermission && fetchData();

        return () => {
            isSubscribed = false;
            cancelSource.cancel(API_IS_BEING_CANCELLED);
        };

        // eslint-disable-next-line
    }, [
        resource,
        query,
        exceptions,
        hasOnlyInternalCompanies,
        hasUploadedFile,
        hasNoPermission,
        isRootRequired,
        isCompanyIdRequired
    ]);

    return { isLoading, options, query, setQuery };
};
